import React from "react";
import { AdminGroup } from "../common/AdminGroup";
import ResourceActionButtons from "./ResourceActionButtons";
import { ResourceCategory } from "../../definitions";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import { Badge, Button, Descriptions, Input, Tooltip } from "antd";
import { format } from "../common/utils";

const { Search } = Input;

// What for ?
export const setFieldsBasedOnResourceType = (data) => {
  if (
    data.resourceCategory &&
    data.resourceCategory === ResourceCategory.official
  ) {
    return { ...data, reassignable: true, autoReassign: true };
  }
  return { ...data, reassignable: false };
};

export const getResourceDisplayColumns = (
  onDelete,
  onApprove,
  onOwnerChange,
  onSelectNewAdminGroup,
  identityId,
  managementUrlTemplate = undefined,
  accessUrlTemplate = undefined,
  adminPanel = false,
  onSearchOwner,
  onSearch,
) => {
  return Object.freeze([
    ...(!adminPanel
      ? [
          {
            width: 50,
            dataIndex: "ownerId",
            render: (ownerId) => {
              return ownerId === identityId ? (
                <Tooltip title="You own this resource">
                  <UserOutlined />
                </Tooltip>
              ) : (
                <Tooltip title="You are administrator of this resource">
                  <TeamOutlined />
                </Tooltip>
              );
            },
            filters: [
              {
                text: "Resources I own",
                value: "owner",
              },
              {
                text: "Resources I am administrator of",
                value: "administrator",
              },
            ],
            onFilter: (value, record) => {
              if (value === "owner") return record.ownerId === identityId;
              else return record.ownerId !== identityId;
            },
          },
        ]
      : []),
    {
      // title: "Name",
      title: (
        <Search
          placeholder="Name"
          allowClear
          onChange={(e) => onSearch(e.target.value)}
          onPressEnter={(e) => onSearch(e.target.value)}
          onClear={() => onSearch()}
        />
      ),
      dataIndex: "displayName",
      render: (displayName, record) => {
        if (record.accessUrl)
          return (
            <Button type="link" href={record.accessUrl} target="_blank">
              {displayName}
            </Button>
          );
        if (accessUrlTemplate) {
          const href = format(accessUrlTemplate, record);
          return (
            <Button type="link" href={href} target="_blank">
              {displayName}
            </Button>
          );
        }
        return displayName;
      },
    },
    {
      title: "Category",
      dataIndex: "resourceCategory",
      filters: [
        {
          text: "Official",
          value: "Official",
        },
        {
          text: "Personal",
          value: "Personal",
        },
      ],
      onFilter: (value, record) => {
        return record.resourceCategory === value;
      },
    },
    {
      title: "State",
      dataIndex: "internalState",
      filters: [
        {
          text: "Invalid",
          value: "Invalid",
        },
        {
          text: "Created",
          value: "Created",
        },
        {
          text: "Active",
          value: "Active",
        },
        {
          text: "ActionRequired",
          value: "ActionRequired",
        },
        {
          text: "GracePeriod",
          value: "GracePeriod",
        },
        {
          text: "Archived",
          value: "Archived",
        },
        {
          text: "Deleted",
          value: "Deleted",
        },
      ],
      onFilter: (value, record) => {
        return record.internalState === value;
      },
      render: (internalState, record) => {
        return (
          <Badge
            status={record.blocked ? "error" : "success"}
            text={internalState}
          />
        );
      },
    },
    // ...(adminPanel
    //   ? [
    {
      title: (
        <Search
          placeholder="Owner"
          allowClear
          onChange={(e) => onSearchOwner(e.target.value)}
          onPressEnter={(e) => onSearchOwner(e.target.value)}
          onClear={() => onSearchOwner()}
        />
      ),
      width: 150,
      dataIndex: "owner",
      render: (owner) => {
        return owner ? owner.upn : "";
      },
    },
    //   ]
    // : []),
    {
      title: "Admin. Group",
      dataIndex: "administrators",
      render: function AdminGroupColumn(administrators) {
        return administrators ? (
          <AdminGroup selectedGroup={administrators} />
        ) : (
          <span>None</span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "right",
      render: function ActionButtonsColumn(id, obj) {
        return (
          <ResourceActionButtons
            item={obj}
            onDelete={onDelete}
            onApprove={onApprove}
            onEditOwner={onOwnerChange}
            onSelectNewAdminGroup={onSelectNewAdminGroup}
            managementUrlTemplate={managementUrlTemplate}
          />
        );
      },
    },
  ]);
};

export const isBuiltinResourceType = (managedResourceTypeName) => {
  return ["account", "group", "application", "identity"].includes(
    managedResourceTypeName.toLowerCase(),
  );
};

export const getResourceDetails = (record, admin = false) => {
  return (
    <>
      <p style={{ margin: 0 }}>{record.description}</p>
      <Descriptions
        title={`${record.displayName} resource details`}
        bordered
        items={[
          {
            label: (
              <Tooltip title="Unique identifier of the resource in this resource Type">
                Resource Identifier
              </Tooltip>
            ),
            children: record.resourceIdentifier
              ? record.resourceIdentifier
              : record.uniqueIdentifier
                ? record.uniqueIdentifier
                : record.upn
                  ? record.upn
                  : "-",
            span: "filled",
          },
          // { label: 'reassignable', children: <Badge status={record.reassignable ? "success" : "error"} text={`${record.reassignable}`} /> },
          // { label: 'autoReassign', children: <Badge status={record.autoReassign ? "success" : "error"} text={`${record.autoReassign}`} /> },
          // { label: 'pendingAction', children: <Badge status={record.pendingAction ? "success" : "error"} text={`${record.pendingAction}`} /> },
          {
            label: "Disabled",
            children: (
              <Badge
                status={!record.disabled ? "success" : "error"}
                text={`${record.disabled}`}
              />
            ),
          },
          ...(record.disabled
            ? [
                {
                  label: "Security Issues",
                  children: (
                    <Badge
                      status={record.securityIssues ? "success" : "error"}
                      text={`${record.securityIssues}`}
                    />
                  ),
                },
                // { label: 'blocked', children: <Badge status={record.blocked ? "success" : "error"} text={`${record.blocked}`} /> },
                {
                  label: "Blocking Time",
                  children: record.blockingTime,
                },
                {
                  label: "Blocking Reason",
                  children: record.blockingReason,
                  span: "filled",
                },
              ]
            : []),
          //internalState render for Service Desk and Admins only
          ...(admin
            ? [
                {
                  label: "Internal State",
                  children: (
                    <Badge
                      status={
                        record.internalState === "Active" ? "success" : "error"
                      }
                      text={`${record.internalState}`}
                    />
                  ),
                },
              ]
            : []),
          {
            label: "State",
            children: (
              <Badge
                status={record.state === "Active" ? "success" : "error"}
                text={`${record.state}`}
              />
            ),
          },
          {
            label: "Blocking Deadline",
            children: record.blockingDeadline,
          },
          {
            label: "Archive Deadline",
            children: record.archiveDeadline,
          },
          { label: "Delete Deadline", children: record.deleteDeadline },
          // {
          //   label: "Expiration Deadline",
          //   children: record.expirationDeadline,
          // },
        ]}
      />
    </>
  );
};
